import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { HealthComLocation } from '../types/HealthComLocation.type';
import { formatPhone } from './format';

// The UTC plugin is idempotent to register, so doing it here removes
// any dependency on the caller also having extended it.
dayjs.extend(utc);

// Only the address/contact fields are read when rendering the event, so
// the input asks for just those. A full `HealthComLocation` satisfies it
// (callers can pass one as-is), and the backend can construct the subset
// directly from a `ClinicLocations` row without faking the rest.
export type IcsLocation = Pick<
  HealthComLocation,
  'address1' | 'address2' | 'city' | 'state' | 'zip' | 'country' | 'phone' | 'site'
>;

export type IcsInput = {
  appointmentId: string;
  startTimestamp: number | string;
  durationMinutes?: number;
  providerName?: string;
  treatmentName?: string;
  patientName?: string;
  location: IcsLocation;
};

// ICS requires TEXT-type properties to escape backslashes, commas,
// semicolons, and newlines. Line breaks must become the literal `\n`
// sequence. Spec: RFC 5545 § 3.3.11.
const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\n|\r/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

// UTC datetime stamp in the form RFC 5545 wants: YYYYMMDDTHHMMSSZ.
const toIcsUtc = (d: dayjs.Dayjs): string => d.utc().format('YYYYMMDDTHHmmss') + 'Z';

const buildAddress = (location: IcsLocation): string => {
  return [
    location.address1,
    location.address2,
    [location.city, location.state].filter(Boolean).join(', '),
    location.zip,
    location.country && location.country !== 'USA' ? location.country : null,
  ]
    .filter(Boolean)
    .join(', ');
};

const buildDescription = (input: IcsInput): string => {
  const lines: string[] = [];

  if (input.treatmentName) {
    lines.push(`Treatment: ${input.treatmentName}`);
  }

  if (input.providerName) {
    lines.push(`Provider: ${input.providerName}`);
  }

  if (input.patientName) {
    lines.push(`For: ${input.patientName}`);
  }

  if (input.location.phone) {
    lines.push(`Phone: ${formatPhone(input.location.phone) || input.location.phone}`);
  }

  if (input.location.site) {
    lines.push(`Website: ${input.location.site}`);
  }

  return lines.join('\n');
};

/**
 * Builds an RFC 5545 VCALENDAR/VEVENT string for a single appointment.
 *
 * Fields chosen so popular clients (Apple Calendar, Google Calendar,
 * Outlook) light up their "get directions" / "call" affordances —
 * specifically LOCATION is the full mailing address, and DESCRIPTION
 * includes phone + website so tapping the event in Apple/Google opens
 * the phone dialer.
 */
export const buildAppointmentIcs = (input: IcsInput): string => {
  const start = dayjs(
    typeof input.startTimestamp === 'number' ? input.startTimestamp : input.startTimestamp
  );
  const end = start.add(input.durationMinutes ?? 30, 'minute');
  const summary = [input.providerName, input.treatmentName ? `(${input.treatmentName})` : null]
    .filter(Boolean)
    .join(' ');
  const address = buildAddress(input.location);
  const description = buildDescription(input);

  // Per RFC 5545: CRLF line terminators, `PRODID` identifies the
  // generator, `UID` must be unique per-event.
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//HealthCom//Appointment//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${input.appointmentId}@healthcom`,
    `DTSTAMP:${toIcsUtc(dayjs())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeIcsText(summary || 'Appointment')}`,
  ];

  if (address) {
    lines.push(`LOCATION:${escapeIcsText(address)}`);
  }

  if (description) {
    lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  }

  lines.push('STATUS:CONFIRMED');
  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.join('\r\n');
};
